import {
    ChangeDetectionStrategy,
    Component,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { ITextLabel, ITextLabelValue } from '@fi-app/fi-translation/fi-translation';
import { interval, Subject } from 'rxjs';
import { debounce, takeUntil } from 'rxjs/operators';
import { FiContentLanguageSharedService } from '@fi-shared/fi-content-language-shared/fi-content-language-shared.service';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';
import { FiApplicationLanguageType } from '@fi-shared/fi-content-language-shared/fi-application-language-type';
import { FiTranslationService } from '@fi-app/fi-translation/fi-translation.service';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { AppTextService } from '@fi-shared/app-text';
import { FiInfiniteScrollDataSource } from '@fi-shared/fi-items-list/fi-infinite-scroll-data-source';
import { ObjectTypes } from '@fi-app/shared/types';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiToggleSwitchLabelAlign } from '@fi-shared/fi-toggle-switch/fi-toggle-switch.component';
import { ProgressType } from '@fi-shared/fi-signal-r/fi-signal-enums';
import { FiSpinnerService } from '@fi-shared/fi-spinner/fi-spinner.service';
import { FiImportLabelsDialogComponent } from '@fi-app/fi-translation/fi-import-labels-dialog/fi-import-labels-dialog.component';
import { cloneDeep, isDefined } from '@fi-shared/utils';
import { comparator } from '@fi-shared/utils/array-sort-comparators';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { coerceNumberProperty } from '@angular/cdk/coercion';
import { FiApplicationLanguageLabelType } from '@fi-app/fi-translation/fi-application-language-label-type';

enum FiTranslationSearchMode {
    Common = 0,
    Strict = 1,
    RegExp = 2,
}

const APPLICATION_LANGUAGES_ORDER = [
    FiApplicationLanguageType.English,
    FiApplicationLanguageType.Swedish,
    FiApplicationLanguageType.Norwegian,
    FiApplicationLanguageType.Spanish, // 2886 translations
    FiApplicationLanguageType.German, // 2932 translations
    FiApplicationLanguageType.Italian, // 2556 translations
    FiApplicationLanguageType.Estonian, // 1323 translations
    FiApplicationLanguageType.Catalan, // 0 translations
    FiApplicationLanguageType.Danish, // 3 translations
    FiApplicationLanguageType.Finnish, // 0 translations
    FiApplicationLanguageType.French, // 0 translations
    FiApplicationLanguageType.Chinese,
    FiApplicationLanguageType.Mongol,
    FiApplicationLanguageType.Dutch,
    FiApplicationLanguageType.Arabic,
    FiApplicationLanguageType.Vietnamese,
    FiApplicationLanguageType.Icelandic,
    FiApplicationLanguageType.Turkish,
];

@Component({
    selector: 'fi-translation-list',
    templateUrl: './fi-translation-list.component.html',
    styleUrls: ['./fi-translation-list.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-translation-list' },
    standalone: false,
})
export class FiTranslationListComponent extends FiInfiniteScrollDataSource<ITextLabel> implements OnInit, OnDestroy {
    @Input() textLabels: ITextLabel[];
    @Input() globalDatabases: string[];

    globalDatabasesDropdownData: { Id: string; Name: string }[];
    sourceGlobalDatabase: string;
    targetGlobalDatabase: string;

    itemsListType: FiItemsListType.Translations;
    objectType: ObjectTypes;
    FiToggleSwitchLabelAlign = FiToggleSwitchLabelAlign;
    FiTranslationSearchMode = FiTranslationSearchMode;

    searchText: string = '';

    searchTextTyping$ = new Subject<boolean>();
    private _searchTextChanged$ = new Subject<void>();
    applicationLanguages: INameIdObj[];
    selectedLanguageIds = [FiApplicationLanguageType.Swedish, FiApplicationLanguageType.English];
    searchMode = FiTranslationSearchMode.Common;
    currentTextLabel: ITextLabel;
    fullWidthMode = false;

    constructor(
        private fiContentLanguageService: FiContentLanguageSharedService,
        private translationService: FiTranslationService,
        private fiDialogsService: FiDialogsService,
        private appTextService: AppTextService,
        private userDataService: FiUserDataService,
        private fiSpinnerService: FiSpinnerService,
        private fiStateService: FiStateService,
        injector: Injector,
    ) {
        super(injector, {});
    }

    ngOnInit(): void {
        this.parseStateParams();
        this.applicationLanguages = this.fiContentLanguageService.getAllApplicationLanguages().sort(
            comparator((item) => {
                const idx = APPLICATION_LANGUAGES_ORDER.indexOf(item.Id);
                return idx !== -1 ? idx : 1000;
            }),
        );
        this.textLabels.forEach((textLabel) => {
            this.applicationLanguages.forEach((appLanguage) => {
                if (!isDefined(textLabel.Values[appLanguage.Id])) {
                    textLabel.Values[appLanguage.Id] = {
                        Value: '',
                    };
                }
            });
        });
        this.reloadData();

        this._searchTextChanged$
            .pipe(takeUntil(this.ngOnDestroy$))
            .pipe(debounce(() => interval(400)))
            .subscribe(() => {
                this.searchTextTyping$.next(false);
                this.updateStateParams();
                this.reloadData();
            });

        this.globalDatabasesDropdownData = this.globalDatabases.map((item) => ({ Id: item, Name: item }));
        this.targetGlobalDatabase = this.globalDatabases.length > 0 ? this.globalDatabases[0] : undefined;
        this.sourceGlobalDatabase = this.globalDatabases.length > 1 ? this.globalDatabases[1] : undefined;
    }

    hasEditAccess(): boolean {
        return this.userDataService.userData.HasMasterIpAddress;
    }

    onSearchTextChanged() {
        this.searchTextTyping$.next(true);
        this._searchTextChanged$.next();
    }

    getListData(): Promise<ITextLabel[]> {
        const filteredTextLabels = this.getFilteredTextLabels();
        return Promise.resolve(filteredTextLabels);
    }

    resetSearchText(initialValue = '') {
        this.searchText = initialValue;
        this.onSearchTextChanged();
    }

    private getFilteredTextLabels(): ITextLabel[] {
        return this.searchText ? this.textLabels.filter(this.filterTextLabelCallbackFn) : this.textLabels;
    }

    private filterTextLabelCallbackFn: (textLabel: ITextLabel) => boolean = (textLabel: ITextLabel) => {
        // always show label if not saved or empty search text
        if (!isDefined(textLabel.TextId) || !this.searchText) {
            return true;
        }
        const str = this.searchText.toLowerCase();
        if (this.searchMode === FiTranslationSearchMode.Common) {
            return this.fullModeFilter(textLabel, str);
        }
        if (this.searchMode === FiTranslationSearchMode.Strict) {
            return this.strictByValueModeFilter(textLabel, str);
        }
        if (this.searchMode === FiTranslationSearchMode.RegExp) {
            return this.regExpModeFilter(textLabel, str);
        }
    };

    private fullModeFilter(textLabel: ITextLabel, searchText: string): boolean {
        if (textLabel.TextId.toString() === searchText) {
            return true;
        }
        if (textLabel.Name && textLabel.Name.toLowerCase().indexOf(searchText) !== -1) {
            return true;
        }
        return this.isValueMeetsFilter(textLabel, (v) => v.Value && v.Value?.toLowerCase().indexOf(searchText) !== -1);
    }

    private strictByValueModeFilter(textLabel: ITextLabel, searchText: string): boolean {
        if (textLabel.TextId.toString() === searchText) {
            return true;
        }
        if (textLabel.Name && textLabel.Name.toLowerCase() === searchText) {
            return true;
        }

        return this.isValueMeetsFilter(textLabel, (v) => v.Value && v.Value?.toLowerCase() === searchText);
    }

    private regExpModeFilter(textLabel: ITextLabel, searchText: string): boolean {
        const regExp = new RegExp(searchText, 'i');
        if (textLabel.Name && regExp.test(textLabel.Name)) {
            return true;
        }
        return this.isValueMeetsFilter(textLabel, (v) => v.Value && regExp.test(v.Value));
    }

    private isValueMeetsFilter(textLabel: ITextLabel, filterFn: (v: ITextLabelValue) => boolean): boolean {
        const result = this.selectedLanguageIds.find((languageId) => filterFn(textLabel.Values[languageId]));
        return !!result;
    }

    onAddTextLabelClick() {
        const newTextLabel: ITextLabel = {
            TextId: undefined,
            Name: '',
            IsDeleted: false,
            IsWeb: false,
            IsFlutter: false,
            IsBackend: false,
            LabelType: FiApplicationLanguageLabelType.Common,
            Values: {},
        };
        this.applicationLanguages.forEach((appLanguage) => {
            newTextLabel.Values[appLanguage.Id] = {
                Value: '',
            };
        });
        this.textLabels.unshift(newTextLabel);
        this.reloadData();
    }

    onTextLabelChanged(textLabel: ITextLabel, changedTextLabel: ITextLabel) {
        const idx = this.textLabels.indexOf(textLabel);
        if (idx !== -1) {
            // should always be true
            this.textLabels[idx] = changedTextLabel;
        }
        this.reloadData();
        this.translationService.invalidateApplicationLanguageLabelsCache();
    }

    onTextLabelDeleteClick(textLabel: ITextLabel) {
        this.deleteTextLabel(textLabel).then(() => {
            textLabel.IsDeleted = true;
            const idx = this.textLabels.indexOf(textLabel);
            this.textLabels[idx] = cloneDeep(this.textLabels[idx]);
            this.reloadData();
            this.translationService.invalidateApplicationLanguageLabelsCache();
        });
    }

    private deleteTextLabel(textLabel: ITextLabel): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (isDefined(textLabel.TextId)) {
                this.fiDialogsService
                    .confirm({
                        headerText: this.appTextService.get('common.warning'),
                        messageText: this.appTextService.get('translation.delelete_text_label_confirm'),
                    })
                    .onYesButtonClick(() => {
                        this.translationService.deleteTextLabel(textLabel).then(resolve, reject);
                    })
                    .onNoButtonClick(() => reject());
            } else {
                // just added label, delete without confirm
                resolve();
            }
        });
    }

    private prevSearchMode = FiTranslationSearchMode.Common;
    onSearchModeChanged() {
        if (this.searchMode === FiTranslationSearchMode.Common || this.searchMode === FiTranslationSearchMode.Strict) {
            if (this.prevSearchMode === FiTranslationSearchMode.RegExp) {
                this.resetSearchText();
            } else {
                // when switch from common to strict and vice versa just repeat search
                this.onSearchTextChanged();
            }
        } else if (this.searchMode === FiTranslationSearchMode.RegExp) {
            this.resetSearchText('^$');
        }
        this.prevSearchMode = this.searchMode;
        this.updateStateParams();
    }

    onLanguageIdsChanged() {
        this.updateStateParams();
    }

    private updateStateParams() {
        this.fiStateService.go(
            FiStateNames.Translation.List,
            {
                languages: this.selectedLanguageIds.join(','),
                searchMode: this.searchMode,
                searchText: this.searchText ? this.searchText : undefined,
            },
            {
                location: 'replace',
            },
        );
    }

    private parseStateParams() {
        const params = this.fiStateService.params;
        const languagesParam: string = params.languages;
        if (languagesParam) {
            this.selectedLanguageIds = languagesParam.split(',').map((lang) => coerceNumberProperty(lang));
        }
        const searchModeParam: string = params.searchMode;
        if (searchModeParam) {
            this.searchMode = coerceNumberProperty(searchModeParam);
        }
        const searchTextParam: string = params.searchText;
        if (searchTextParam) {
            this.searchText = searchTextParam;
        }
    }

    onExportLabelsClick() {
        this.translationService.downloadTextLabelsExcel();
    }

    onImportLabelsClick() {
        this.fiDialogsService.custom({
            contentComponentRef: FiImportLabelsDialogComponent,
            yesButtonText: this.appTextService.get('common.import'),
            noButtonText: this.appTextService.get('common.close'),
            config: {
                minWidth: '80%',
            },
        });
    }

    onCopyLabelsClick() {
        this.fiDialogsService
            .confirm({
                headerText: this.appTextService.get('common.warning'),
                messageText: this.appTextService.get('translation.text_labels_danger_operation'),
            })
            .onYesButtonClick(() => {
                const promise = this.translationService.copyTextLabels(
                    this.sourceGlobalDatabase,
                    this.targetGlobalDatabase,
                );
                this.fiSpinnerService.trackPromise(promise, {
                    headerText: this.appTextService.get('translation.copy_labels'),
                    signalRProgressType: ProgressType.CopyTextLabels,
                });
                promise.then(() => {
                    this.translationService.invalidateApplicationLanguageLabelsCache();
                });
            });
    }

    isCopyLabelsDisabled() {
        return !(
            this.sourceGlobalDatabase &&
            this.targetGlobalDatabase &&
            this.sourceGlobalDatabase !== this.targetGlobalDatabase
        );
    }

    onContainerClick($event: MouseEvent) {
        this.currentTextLabel = undefined;
    }

    onItemCLick($event: MouseEvent, textLabel: ITextLabel) {
        this.currentTextLabel = textLabel;
        $event.stopPropagation();
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
